import { arrayOf, bool, func, number, shape, string, oneOf, instanceOf, oneOfType } from 'prop-types'
import { ANSWER_TYPES } from '../helpers/answer_types'
import FORWARD from '../helpers/forward_button_types'

const ActivityOptionType = shape({
  activityId: oneOfType([string, number]),
  optionId: oneOfType([string, number]),
  title: string,
})

export const ActivityGroupAnswersType = shape({
  activityId: oneOfType([string, number]),
  id: oneOfType([string, number]),
  createDate: string,
  cisUserId: string,
})

export const ActivityType = shape({
  activityGroupId: oneOfType([string, number]),
  activityId: oneOfType([string, number]),
  options: arrayOf(ActivityOptionType),
  points: number,
  title: string,
  type: string,
  startRange: number,
  endRange: number,
})

const FormFieldElementType = shape({
  formFieldType: string,
  dataProperty: string,
  label: string,
  placeholder: string,
})

export const FormFieldActivityType = shape({
  activityGroupId: oneOfType([string, number]),
  activityId: oneOfType([string, number]),
  formFields: arrayOf(FormFieldElementType),
  rank: number,
  photoLimit: number,
  title: string,
  type: string,
})

export const AnswerDataType = shape({
  dateWritten: instanceOf(Date),
  addAnswer: func,
  answered: bool,
  resetAnswer: func,
  selectedImgUrl: string,
  text: string,
})

export const ColorThemeType = oneOf(['blue', 'green', 'yellow', 'red', 'purple'])

export const SplashType = oneOf(['single', 'multi', 'external', 'photo_timeline', 'informational', 'bucket-list'])

export const ActivityGroupType = shape({
  activityGroupId: oneOfType([string, number]).isRequired,
  activityGroupPoints: number.isRequired,
  categoryId: oneOfType([string, number]).isRequired,
  colorTheme: ColorThemeType.isRequired,
  parentActivityGroupId: oneOfType([string, number]),
  imageUrlSvg: string,
  splash: SplashType,
  subtitle: string,
  title: string,
  // Extended fields actually consumed in components (optional)
  activityGroupColor: shape({
    splashBackground: string,
    background: string,
    primary: string,
    card: shape({ primary: string }),
  }),
  person: shape({ name: string }),
  visibleId: oneOfType([string, number]),
})

export const BackgroundFillsType = shape({
  base: string,
  midLight: string,
  light: string,
  dark15: string,
  lessDark: string,
  light05: string,
  light02: string,
})

export const ColorObj = shape({
  background: string.isRequired,
  button: string.isRequired,
  card: shape({
    primary: string.isRequired,
    // Optional secondary color used in some invite flows
    secondary: string,
  }),
  text: string.isRequired,
  // Optional extended keys used in splash components
  splashBackground: string,
  primary: string,
  // Additional optional transparent token (e.g., colors.transparent.gray70)
  transparent: string,
})

// Common animation style fragment for react-spring animated value objects
export const AnimationStyleType = shape({
  opacity: oneOfType([number, shape({})]),
  x: oneOfType([number, shape({})]),
  y: oneOfType([number, shape({})]),
  scale: oneOfType([number, shape({})]),
})

export const OverlayType = shape({
  close: func,
  handleClick: func,
  handleTransitioned: func,
  inline: bool,
  isOpen: bool,
  minWidth: string,
  open: func,
  transitioning: bool,
})

export const ActivitiesWithAnswersType = arrayOf(
  shape({
    activityGroupId: oneOfType([string, number]).isRequired,
    activityId: oneOfType([string, number]).isRequired,
    points: number,
    savedAnswer: shape({
      activityId: oneOfType([string, number]).isRequired,
      id: oneOfType([string, number]),
      title: string,
    }),
    title: string,
    type: oneOf([
      ANSWER_TYPES.QUESTION_ANSWER,
      ANSWER_TYPES.FORM_FIELD,
      ANSWER_TYPES.QUESTION_ANSWER_PHOTOS,
      ANSWER_TYPES.SELECT_SINGLE,
    ]),
  })
)

export const AvatarType = shape({
  avatarId: string.isRequired,
  forPersonId: string.isRequired,
  avatarUrl: string.isRequired,
  createdDate: string.isRequired,
  updatedDate: string,
  active: bool,
})

export const CarouselNavOptionsType = shape({
  back: bool,
  forwardMode: oneOf([FORWARD.DONE, FORWARD.NEXT, FORWARD.SKIP]),
  forwardDisabled: bool,
})
